import { apiRequest } from "@/lib/queryClient";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Bell, CheckCircle, Calendar, Users, Shield, Settings2, Loader2,
  Plus, UserCheck, UserX, Edit, Link2, Download, Database,
  Mail, Zap, ExternalLink, Check,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { ClaudeConnection } from "@/components/settings/ClaudeConnection";

// ─── Types ──────────────────────────────────────────

interface UserSettings {
  emailNotifications: boolean;
  notifyEmailOpened: boolean;
  notifyTaskDue: boolean;
  notifyDealUpdate: boolean;
  notifyMention: boolean;
  notifySystem: boolean;
  emailTonePreset: string;
  emailToneCustom: string;
}

interface TeamUser {
  id: string; username: string; name: string | null; email: string | null;
  initials: string | null; isAdmin: boolean | null; canViewFinancials: boolean | null;
  isActive: boolean | null; lastLoginAt: string | null; createdAt: string | null;
}

interface IntegrationStatus {
  microsoft: { connected: boolean; email: string | null };
  apollo: { configured: boolean };
  ai: { configured: boolean };
  outlookAddin: { version: string };
}

// ─── Chrome Extension API Key ───────────────────────

function ChromeExtensionKey() {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch existing key on mount
  const { } = useQuery({
    queryKey: ["extension-api-key"],
    queryFn: async () => {
      const res = await fetch("/api/extension/api-key", { credentials: "include" });
      if (!res.ok) return null;
      const data = await res.json();
      setApiKey(data.apiKey || null);
      return data;
    },
  });

  const generateKey = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/extension/generate-api-key", {
        method: "POST", credentials: "include",
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setApiKey(data.apiKey);
      toast.success("API key generated");
    } catch {
      toast.error("Failed to generate key");
    } finally {
      setLoading(false);
    }
  };

  const copyKey = () => {
    if (!apiKey) return;
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Download className="w-4 h-4 text-green-600 dark:text-green-400" />
          <div>
            <p className="text-sm font-medium">Chrome Extension</p>
            <p className="text-xs text-muted-foreground">
              {apiKey ? "API key active" : "Generate a key to connect"}
            </p>
          </div>
        </div>
        {apiKey ? (
          <div className="flex items-center gap-2">
            <code className="text-[11px] bg-muted px-2 py-1 rounded font-mono">
              {apiKey.substring(0, 8)}...{apiKey.substring(apiKey.length - 4)}
            </code>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyKey}>
              {copied ? <Check className="w-3 h-3" /> : "Copy"}
            </Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={generateKey} disabled={loading}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Generate Key"}
          </Button>
        )}
      </div>
      <div className="rounded-md border p-3 text-xs text-muted-foreground space-y-1.5">
        <a href="/first-turn-crm-extension.zip" download className="font-medium text-foreground underline underline-offset-2">
          Download the extension
        </a>
        <ol className="list-decimal pl-4 space-y-0.5">
          <li>Unzip it.</li>
          <li>In Chrome, open chrome://extensions, turn on Developer mode, click Load unpacked and pick the unzipped folder.</li>
          <li>Click the extension's icon, open its settings and paste the key above.</li>
        </ol>
      </div>
    </div>
  );
}

// ─── Component ──────────────────────────────────────

export default function Settings() {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [editingName, setEditingName] = useState(false);
  const [nameText, setNameText] = useState("");
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: "", email: "", isAdmin: false, canViewFinancials: false });
  const [editingUser, setEditingUser] = useState<TeamUser | null>(null);

  // ─── Settings query ───
  const { data: settings } = useQuery<UserSettings>({
    queryKey: ["user-settings"],
    queryFn: async () => { const r = await fetch("/api/users/me/settings", { credentials: "include" }); return r.ok ? r.json() : null; },
  });

  const saveSetting = useMutation({
    mutationFn: async (update: Partial<UserSettings>) => {
      await apiRequest("PATCH", "/api/users/me/settings", update);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["user-settings"] }),
    onError: () => toast.error("Could not save settings. Your previous settings have been restored."),
  });

  const toggle = (key: keyof UserSettings) => {
    if (!settings || saveSetting.isPending) return;
    const newVal = !settings[key];
    queryClient.setQueryData(["user-settings"], { ...settings, [key]: newVal });
    saveSetting.mutate({ [key]: newVal });
  };

  // ─── Integrations query ───
  const { data: integrations } = useQuery<IntegrationStatus>({
    queryKey: ["integrations-status"],
    queryFn: async () => { const r = await fetch("/api/integrations/status", { credentials: "include" }); return r.ok ? r.json() : null; },
  });

  // ─── Admin: Team users ───
  const { data: teamUsers = [] } = useQuery<TeamUser[]>({
    queryKey: ["admin-users"],
    queryFn: async () => { const r = await fetch("/api/admin/users", { credentials: "include" }); return r.ok ? r.json() : []; },
    enabled: !!isAdmin,
  });

  const { data: dataStats } = useQuery({
    queryKey: ["admin-data-stats"],
    queryFn: async () => { const r = await fetch("/api/admin/data-stats", { credentials: "include" }); return r.ok ? r.json() : null; },
    enabled: !!isAdmin,
  });

  const createUser = useMutation({
    mutationFn: async (data: typeof newUser) => {
      const r = await fetch("/api/admin/users", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, username: data.email, initials: data.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) }) });
      if (!r.ok) throw new Error();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["admin-users"] }); setAddUserOpen(false); setNewUser({ name: "", email: "", isAdmin: false, canViewFinancials: false }); toast.success("User created"); },
    onError: () => toast.error("Failed to create user"),
  });

  const updateUser = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: any }) => {
      const r = await fetch(`/api/admin/users/${id}`, { method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updates) });
      if (!r.ok) throw new Error();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["admin-users"] }); setEditingUser(null); toast.success("User updated"); },
    onError: () => toast.error("Could not update user. Please try again."),
  });

  const [savingName, setSavingName] = useState(false);
  const [changingUser, setChangingUser] = useState<string | null>(null);
  const toggleActive = async (id: string, activate: boolean) => {
    if (changingUser) return;
    setChangingUser(id);
    try {
    await apiRequest("PATCH", `/api/admin/users/${id}/${activate ? "activate" : "deactivate"}`);
    queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    toast.success(activate ? "User activated" : "User deactivated");
    } catch { toast.error("Could not change user access. Please try again."); }
    finally { setChangingUser(null); }
  };

  const saveName = async () => {
    if (!nameText.trim() || savingName) return;
    setSavingName(true);
    try {
    await apiRequest("PATCH", "/api/users/me/profile", { name: nameText.trim() });
    queryClient.invalidateQueries({ queryKey: ["auth-status"] });
    setEditingName(false);
    toast.success("Name updated");
    } catch { toast.error("Could not update your name. Your draft is still here; try again."); }
    finally { setSavingName(false); }
  };

  if (!settings) return <AppLayout title="Settings"><div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div></AppLayout>;

  return (
    <AppLayout title="Settings">
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your account, notifications, and preferences</p>
        </div>

        <h2 className="pt-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">You</h2>

        {/* ─── PROFILE ─── */}
        <div className="border rounded-lg">
          <div className="flex items-center gap-2 border-b bg-muted/40 px-5 py-2.5">
            <Settings2 className="w-4 h-4" />
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Profile</h2>
          </div>
          <div className="px-5 py-4">
            <div className="grid grid-cols-2 gap-y-4 gap-x-8 text-sm">
              <div>
                <span className="text-xs text-muted-foreground block mb-1">Name</span>
                {editingName ? (
                  <div className="flex gap-2">
                    <Input aria-label="Your name" value={nameText} onChange={(e) => setNameText(e.target.value)} className="h-8 text-sm" autoFocus />
                    <Button size="sm" className="h-8" disabled={savingName} onClick={saveName}>Save</Button>
                    <Button size="sm" variant="outline" className="h-8" disabled={savingName} onClick={() => setEditingName(false)}>Cancel</Button>
                  </div>
                ) : (
                  <button type="button" aria-label="Edit your name" className="font-medium hover:text-primary-text focus-visible:ring-2 focus-visible:ring-ring" onClick={() => { setNameText(user?.name || ""); setEditingName(true); }}>{user?.name || "—"} <Edit className="w-3 h-3 inline text-muted-foreground" /></button>
                )}
              </div>
              <div>
                <span className="text-xs text-muted-foreground block mb-1">Email</span>
                <span className="font-medium">{user?.email || "—"}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block mb-1">Status</span>
                <Badge className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Active</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* ─── NOTIFICATIONS ─── */}
        <div className="border rounded-lg">
          <div className="flex items-center gap-2 border-b bg-muted/40 px-5 py-2.5">
            <Bell className="w-4 h-4" />
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Notifications</h2>
          </div>
          <div className="px-5 py-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Email Alerts</p>
                <p className="text-xs text-muted-foreground">Receive email when notifications arrive</p>
              </div>
              <Switch checked={settings.emailNotifications} onCheckedChange={() => toggle("emailNotifications")} />
            </div>

            {settings.emailNotifications && (
              <div className="ml-4 pl-4 border-l space-y-3">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Send emails for:</p>
                {[
                  { key: "notifyTaskDue" as const, icon: CheckCircle, label: "Task due" },
                  { key: "notifyDealUpdate" as const, icon: Calendar, label: "Deal updates" },
                  { key: "notifyMention" as const, icon: Users, label: "Mentions" },
                  { key: "notifySystem" as const, icon: Shield, label: "System notifications" },
                ].map(({ key, icon: Icon, label }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Icon className="w-3.5 h-3.5 text-muted-foreground" /><span className="text-sm">{label}</span></div>
                    <Switch checked={settings[key]} onCheckedChange={() => toggle(key)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <h2 className="pt-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Connections</h2>

        {/* ─── CONNECTED ACCOUNTS ─── */}
        <div className="border rounded-lg">
          <div className="flex items-center gap-2 border-b bg-muted/40 px-5 py-2.5">
            <Link2 className="w-4 h-4" />
            <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Connected Accounts</h2>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <div>
                  <p className="text-sm font-medium">Microsoft 365</p>
                  <p className="text-xs text-muted-foreground">{integrations?.microsoft?.email || "Email & calendar sync"}</p>
                </div>
              </div>
              <Badge className={integrations?.microsoft?.connected ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-muted text-muted-foreground"} variant="outline">
                {integrations?.microsoft?.connected ? "Connected" : "Not connected"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <div>
                  <p className="text-sm font-medium">Apollo.io</p>
                  <p className="text-xs text-muted-foreground">Contact & company enrichment</p>
                </div>
              </div>
              <Badge className={integrations?.apollo?.configured ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-muted text-muted-foreground"} variant="outline">
                {integrations?.apollo?.configured ? "Configured" : "Not configured"}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ExternalLink className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <div>
                  <p className="text-sm font-medium">Outlook Add-in</p>
                  <p className="text-xs text-muted-foreground">v{integrations?.outlookAddin?.version || "2.4.0"}</p>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" variant="outline">Installed</Badge>
            </div>
            <ChromeExtensionKey />
            <ClaudeConnection />
          </div>
        </div>

        <h2 className="pt-2 text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Workspace</h2>

        {/* ─── ADMIN: TEAM MANAGEMENT ─── */}
        {isAdmin && (
          <div className="border rounded-lg">
            <div className="px-6 py-3 border-b bg-muted/30 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Team Management</h2>
                <Badge variant="outline" className="text-[10px]">Admin</Badge>
              </div>
              <Button size="sm" className="h-7 text-xs gap-1" onClick={() => setAddUserOpen(true)}><Plus className="w-3 h-3" /> Add User</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/20">
                  <tr>
                    <th className="text-left p-3 font-medium text-xs text-muted-foreground">Name</th>
                    <th className="text-left p-3 font-medium text-xs text-muted-foreground">Email</th>
                    <th className="text-left p-3 font-medium text-xs text-muted-foreground">Status</th>
                    <th className="text-left p-3 font-medium text-xs text-muted-foreground">Last Login</th>
                    <th className="w-20 p-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {teamUsers.map(u => (
                    <tr key={u.id} className="hover:bg-muted/30">
                      <td className="p-3">
                        <div className="font-medium">{u.name || "—"}</div>
                        <div className="flex items-center gap-1.5">
                          {u.isAdmin && <span className="text-[10px] text-amber-600 font-medium dark:text-amber-400">ADMIN</span>}
                          {(u.canViewFinancials || u.isAdmin) && (
                            <span
                              className="text-[10px] text-green-700 font-medium dark:text-green-400"
                              title={u.isAdmin ? "Admins always see financials" : "Can view financials"}
                            >
                              FINANCIALS
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground">{u.email || "—"}</td>
                      <td className="p-3">
                        <Badge className={u.isActive ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"} variant="outline">
                          {u.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td className="p-3 text-xs text-muted-foreground">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : "Never"}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={`Edit ${u.name || u.email}`} onClick={() => setEditingUser(u)}><Edit className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={!!changingUser} aria-label={`${u.isActive ? "Deactivate" : "Activate"} ${u.name || u.email}`} onClick={() => toggleActive(u.id, !u.isActive)}>
                            {u.isActive ? <UserX className="w-3.5 h-3.5 text-red-500" /> : <UserCheck className="w-3.5 h-3.5 text-green-500" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ─── ADMIN: DATA MANAGEMENT ─── */}
        {isAdmin && dataStats && (
          <div className="border rounded-lg">
            <div className="flex items-center gap-2 border-b bg-muted/40 px-5 py-2.5">
              <Database className="w-4 h-4" />
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Data Overview</h2>
              <Badge variant="outline" className="text-[10px]">Admin</Badge>
            </div>
            <div className="px-5 py-4">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div><p className="text-2xl font-bold">{dataStats.companies}</p><p className="text-xs text-muted-foreground">Companies</p></div>
                <div><p className="text-2xl font-bold">{dataStats.contacts}</p><p className="text-xs text-muted-foreground">Contacts</p></div>
                <div><p className="text-2xl font-bold">{dataStats.cois}</p><p className="text-xs text-muted-foreground">COIs</p></div>
                <div><p className="text-2xl font-bold">{dataStats.activities}</p><p className="text-xs text-muted-foreground">Activities</p></div>
              </div>
            </div>
          </div>
        )}

        {/* Archive */}
        <div className="border rounded-lg">
          <div className="px-5 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.09em] text-muted-foreground">Archived Companies</h2>
              <p className="text-xs text-muted-foreground">Deleted companies are recoverable for 30 days</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.location.href = '/archived'}>View Archive</Button>
          </div>
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Full Name</Label>
              <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} placeholder="John Doe" className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Email (Microsoft Account)</Label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="john@company.com" className="h-9" />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={newUser.isAdmin} onChange={(e) => setNewUser({ ...newUser, isAdmin: e.target.checked })} className="rounded" />
                Admin access
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={newUser.isAdmin || newUser.canViewFinancials} disabled={newUser.isAdmin}
                  onChange={(e) => setNewUser({ ...newUser, canViewFinancials: e.target.checked })} className="rounded" />
                View financials (revenue, fees, projections)
              </label>
              {newUser.isAdmin && (
                <p className="text-xs text-muted-foreground pl-6">Admins always see financials.</p>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="ghost" onClick={() => setAddUserOpen(false)}>Cancel</Button>
              <Button onClick={() => createUser.mutate(newUser)} disabled={!newUser.name || !newUser.email}>Add User</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader><DialogTitle>Edit User</DialogTitle></DialogHeader>
          {editingUser && (
            <div className="space-y-3 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Name</Label>
                <Input value={editingUser.name || ""} onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })} className="h-9" />
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!editingUser.isAdmin} onChange={(e) => setEditingUser({ ...editingUser, isAdmin: e.target.checked })} className="rounded" />
                  Admin access
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!(editingUser.isAdmin || editingUser.canViewFinancials)} disabled={!!editingUser.isAdmin}
                    onChange={(e) => setEditingUser({ ...editingUser, canViewFinancials: e.target.checked })} className="rounded" />
                  View financials (revenue, fees, projections)
                </label>
                {editingUser.isAdmin && (
                  <p className="text-xs text-muted-foreground pl-6">
                    Admins always see financials. Remove admin access to control this separately.
                  </p>
                )}
              </div>
              <div className="flex gap-2 justify-end pt-2 border-t">
                <Button variant="ghost" onClick={() => setEditingUser(null)}>Cancel</Button>
                <Button onClick={() => updateUser.mutate({ id: editingUser.id, updates: { name: editingUser.name, isAdmin: editingUser.isAdmin, canViewFinancials: editingUser.canViewFinancials ?? false } })}>Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
